import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Plane, Clock, Shield, Star, Phone, Mail, CheckCircle } from 'lucide-react';

const AirportTransferBrussels = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // SEO Meta tags
    document.title = 'Airport Transfer Brussels | VIP Brussels Airport Chauffeur Service 24/7';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Premium Brussels Airport Transfer Service. Professional chauffeur, luxury vehicles, flight tracking. Book your Brussels Airport transfer 24/7. Starting from €85.');
    }
  }, []);

  const scrollToBooking = () => {
    navigate('/');
    setTimeout(() => {
      const bookingSection = document.getElementById('booking');
      if (bookingSection) {
        bookingSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-black text-white">
      {/* Hero Section */}
      <section className="relative py-24 px-6 text-center bg-gradient-to-r from-black via-gray-900 to-black">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold text-gold mb-6">
            Airport Transfer Brussels
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-8">
            Premium Brussels Airport Chauffeur Service | Available 24/7
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button 
              onClick={scrollToBooking}
              className="bg-gold hover:bg-amber-600 text-black text-lg px-8 py-6"
            >
              Book Airport Transfer Now
            </Button>
            <Button 
              onClick={() => window.location.href = 'tel:+3223150254'}
              variant="outline"
              className="border-gold text-gold hover:bg-gold/20 text-lg px-8 py-6"
            >
              <Phone className="mr-2" /> +32 2 315 02 54
            </Button>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-6 max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 mb-16">
          <div>
            <h2 className="text-3xl font-bold text-gold mb-6">
              Brussels Airport Transfer Service – Reliable & Professional
            </h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-4">
              Luxury Transfer Services offers premium <strong>airport transfer Brussels</strong> service with professional chauffeurs and luxury vehicles. Whether you're arriving at Brussels Airport (BRU) or Charleroi Airport (CRL), our VIP transport ensures a comfortable and stress-free journey.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed mb-4">
              Our <strong>Brussels airport chauffeur service</strong> includes flight tracking, meet & greet at the terminal, and door-to-door service. Perfect for business travelers, VIP guests, and international visitors.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              <strong>Starting from €85</strong> for Brussels Airport transfers.
            </p>
          </div>

          <div>
            <Card className="bg-gray-800/50 border-gold/30">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gold mb-6">Why Choose Our Airport Transfer?</h3>
                <ul className="space-y-4">
                  {[
                    'Flight tracking – No extra charges for delays',
                    'Meet & Greet service at terminal',
                    'Luxury Mercedes & BMW vehicles',
                    'Professional English-speaking drivers',
                    '24/7 availability – Day & night service',
                    'Fixed prices – No hidden fees',
                    'Free waiting time: 60 minutes',
                    'Child seats available on request'
                  ].map((feature, index) => (
                    <li key={index} className="flex items-start gap-3 text-gray-300">
                      <CheckCircle className="w-5 h-5 text-gold flex-shrink-0 mt-1" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Services */}
        <div className="mb-16">
          <h2 className="text-4xl font-bold text-gold mb-8 text-center">
            Our Brussels Airport Transfer Services
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-gray-800/50 border-gold/30 hover:border-gold transition-all">
              <CardContent className="p-6 text-center">
                <Plane className="w-12 h-12 text-gold mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-3">Brussels Airport (BRU)</h3>
                <p className="text-gray-400">
                  Direct transfer from/to Brussels Zaventem Airport. Meet & greet included.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gray-800/50 border-gold/30 hover:border-gold transition-all">
              <CardContent className="p-6 text-center">
                <Clock className="w-12 h-12 text-gold mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-3">Charleroi Airport (CRL)</h3>
                <p className="text-gray-400">
                  Comfortable transfer from Brussels South Charleroi Airport to any destination.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gray-800/50 border-gold/30 hover:border-gold transition-all">
              <CardContent className="p-6 text-center">
                <Shield className="w-12 h-12 text-gold mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-3">Private Terminal Service</h3>
                <p className="text-gray-400">
                  VIP service for private jets and executive travelers at Brussels Airport.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Popular Routes */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gold mb-8">Popular Airport Transfer Routes</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-gray-800/50 border-gold/30">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-white mb-4">From Brussels Airport (BRU)</h3>
                <ul className="space-y-2 text-gray-300">
                  <li>✈️ Brussels Airport → Brussels City Center (€85)</li>
                  <li>✈️ Brussels Airport → Bruges (€180)</li>
                  <li>✈️ Brussels Airport → Antwerp (€130)</li>
                  <li>✈️ Brussels Airport → Ghent (€150)</li>
                  <li>✈️ Brussels Airport → EU Quarter (€90)</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-gray-800/50 border-gold/30">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-white mb-4">From Charleroi Airport (CRL)</h3>
                <ul className="space-y-2 text-gray-300">
                  <li>✈️ Charleroi Airport → Brussels Center (€120)</li>
                  <li>✈️ Charleroi Airport → Bruges (€190)</li>
                  <li>✈️ Charleroi Airport → Antwerp (€150)</li>
                  <li>✈️ Charleroi Airport → Ghent (€140)</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* How It Works */}
        <div className="mb-16 bg-gray-800/50 border border-gold/30 rounded-lg p-8">
          <h2 className="text-3xl font-bold text-gold mb-8 text-center">How to Book Your Airport Transfer</h2>
          <div className="grid md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="w-16 h-16 bg-gold rounded-full flex items-center justify-center mx-auto mb-4 text-black font-bold text-2xl">1</div>
              <h3 className="text-xl font-bold text-white mb-2">Book Online</h3>
              <p className="text-gray-400">Select your pickup date, time & destination</p>
            </div>
            <div>
              <div className="w-16 h-16 bg-gold rounded-full flex items-center justify-center mx-auto mb-4 text-black font-bold text-2xl">2</div>
              <h3 className="text-xl font-bold text-white mb-2">Confirmation</h3>
              <p className="text-gray-400">Instant booking confirmation via email</p>
            </div>
            <div>
              <div className="w-16 h-16 bg-gold rounded-full flex items-center justify-center mx-auto mb-4 text-black font-bold text-2xl">3</div>
              <h3 className="text-xl font-bold text-white mb-2">Flight Tracking</h3>
              <p className="text-gray-400">We monitor your flight for delays</p>
            </div>
            <div>
              <div className="w-16 h-16 bg-gold rounded-full flex items-center justify-center mx-auto mb-4 text-black font-bold text-2xl">4</div>
              <h3 className="text-xl font-bold text-white mb-2">Meet & Ride</h3>
              <p className="text-gray-400">Your chauffeur meets you at arrival</p>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gold mb-8">Frequently Asked Questions</h2>
          <div className="space-y-6">
            <Card className="bg-gray-800/50 border-gold/30">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-white mb-3">How much does Brussels Airport transfer cost?</h3>
                <p className="text-gray-300">
                  Brussels Airport transfer to city center starts from €85. Prices vary based on destination and vehicle type. All prices are fixed with no hidden fees.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gray-800/50 border-gold/30">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-white mb-3">Do you track my flight?</h3>
                <p className="text-gray-300">
                  Yes! We monitor all flights in real-time. If your flight is delayed, your chauffeur will adjust the pickup time automatically at no extra charge.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gray-800/50 border-gold/30">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-white mb-3">Where will the driver meet me?</h3>
                <p className="text-gray-300">
                  Your chauffeur will meet you at the arrival hall with a name board. Exact meeting point details are included in your confirmation email.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center bg-gradient-to-r from-gold/20 to-amber-600/20 border border-gold/50 rounded-lg p-12">
          <h2 className="text-4xl font-bold text-white mb-4">
            Book Your Brussels Airport Transfer Today
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Professional chauffeur service available 24/7 | Starting from €85
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button 
              onClick={scrollToBooking}
              className="bg-gold hover:bg-amber-600 text-black text-lg px-8 py-6"
            >
              Book Now – Instant Confirmation
            </Button>
            <Button 
              onClick={() => navigate('/')}
              variant="outline"
              className="border-gold text-gold hover:bg-gold/20 text-lg px-8 py-6"
            >
              View Our Services
            </Button>
          </div>
          <div className="mt-8 flex items-center justify-center gap-8 text-gray-300">
            <div className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-gold" />
              <span>+32 2 315 02 54</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-gold" />
              <span>contact@luxurytransferservices.be</span>
            </div>
          </div>
        </div>
      </section>

      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Airport Transfer Brussels",
          "provider": {
            "@type": "Organization",
            "name": "Luxury Transfer Services",
            "telephone": "+3223150254",
            "email": "contact@luxurytransferservices.be"
          },
          "areaServed": {
            "@type": "City",
            "name": "Brussels"
          },
          "offers": {
            "@type": "Offer",
            "priceCurrency": "EUR",
            "price": "85.00"
          }
        })}
      </script>
    </div>
  );
};

export default AirportTransferBrussels;
